import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useSnapshot } from 'valtio';

import type { AssetPatchState } from '@cartwhl/common/api/assetPatchState';

import { noticesStore, userStore } from 'stores/global';

import {
  ASSET_LIBRARY_DELETE_OWN_ITEM_TOOLTIP,
  ASSET_LIBRARY_RESTORE_OWN_ITEM_TOOLTIP,
  type AssetLibraryDeleteTarget,
  type AssetLibraryDeletableItem,
  assetMatchesBookmarkRequest,
  canUserDeleteAllAssets,
  canUserDeleteAsset,
  dedupeBookmarkRequests,
  dedupeDeleteTargets,
  findAssetForGroupID,
  patchAssetTarget,
  resolveAssetLibraryBookmarkRequest,
  resolveAssetLibraryDeleteTarget,
  resolveGroupSelectionDeleteTarget,
  setAssetBookmarked,
} from '../assetLibraryAssetLifecycle';
import { getBookmarkReferenceAssets } from '../assetLibraryAssetsLoad';
import { resolveAssetBookmarkedState } from '../assetLibraryGroupIds';
import { useStore } from '../assetLibraryStore';
import AssetLibraryMoveToTrashModal from '../components/AssetLibraryMoveToTrashModal';
import type { AssetLibraryDisplayItem } from '../utils';

type BatchMutationResult = 'success' | 'partial' | 'failed';

type AssetLibraryAssetActionsContextValue = {
  deleteOwnItemTooltip: string;
  restoreOwnItemTooltip: string;
  canDeleteAsset: (asset: AssetLibraryDeletableItem | undefined) => boolean;
  canDeleteAllAssets: (assets: AssetLibraryDeletableItem[]) => boolean;
  requestMoveToTrash: (
    assets: AssetLibraryDisplayItem | AssetLibraryDisplayItem[],
  ) => void;
  requestRestoreFromTrash: (
    assets: AssetLibraryDisplayItem | AssetLibraryDisplayItem[],
  ) => Promise<void>;
  toggleBookmark: (asset: AssetLibraryDisplayItem) => Promise<void>;
  bookmarkAllSelected: () => Promise<void>;
  requestMoveGroupToTrash: () => void;
  requestRestoreGroupFromTrash: () => Promise<void>;
};

const AssetLibraryAssetActionsContext =
  createContext<AssetLibraryAssetActionsContextValue | null>(null);

function finishTrashMutation(
  store: ReturnType<typeof useStore>,
  succeeded: AssetLibraryDeleteTarget[],
) {
  store.markAssetsDeleted(succeeded);
  store.closePreviewsAfterTrash();
  if (store.state.selectedGroup) {
    store.closeGroupDetail();
  }
  store.clearAssetSelection();
}

export function AssetLibraryAssetActionsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const store = useStore();
  const { userInfo } = useSnapshot(userStore.state);
  const userID = userInfo?.userID;
  const [trashModalOpen, setTrashModalOpen] = useState(false);
  const [trashModalLoading, setTrashModalLoading] = useState(false);
  const [pendingDeleteTargets, setPendingDeleteTargets] = useState<
    AssetLibraryDeleteTarget[]
  >([]);

  const getActionContext = useCallback(() => {
    const apiKey = userStore.state.apiKey.data?.apiKey;

    return { apiKey, userID };
  }, [userID]);

  const executePatchTargets = useCallback(
    async (
      targets: AssetLibraryDeleteTarget[],
      state: AssetPatchState,
    ): Promise<BatchMutationResult> => {
      const { apiKey, userID: actionUserID } = getActionContext();
      if (!apiKey || !actionUserID) {
        noticesStore.error('Unable to authenticate. Please try again.');
        return 'failed';
      }

      const succeeded: AssetLibraryDeleteTarget[] = [];
      const isDelete = state === 'DELETE';

      for (const target of targets) {
        const { error } = await patchAssetTarget(target, {
          apiKey,
          userID: actionUserID,
          state,
        });

        if (error) {
          if (succeeded.length > 0) {
            if (isDelete) {
              finishTrashMutation(store, succeeded);
            } else {
              store.markAssetsRestored(succeeded);
            }

            noticesStore.error(
              isDelete
                ? `Moved ${succeeded.length} of ${targets.length} items to trash. Some items could not be moved.`
                : `Restored ${succeeded.length} of ${targets.length} items. Some items could not be restored.`,
            );
            return 'partial';
          }

          noticesStore.error(
            error.message ||
              (isDelete
                ? 'Unable to move item to trash.'
                : 'Unable to restore item.'),
          );
          return 'failed';
        }

        succeeded.push(target);
      }

      if (isDelete) {
        finishTrashMutation(store, succeeded);
      } else {
        store.markAssetsRestored(succeeded);
      }

      return 'success';
    },
    [getActionContext, store],
  );

  const resolveDeleteTargetsFromAssets = useCallback(
    (assets: AssetLibraryDisplayItem[]) =>
      dedupeDeleteTargets(
        assets
          .map((asset) => resolveAssetLibraryDeleteTarget(asset))
          .filter(
            (target): target is AssetLibraryDeleteTarget => target !== null,
          ),
      ),
    [],
  );

  const canMutateGroup = useCallback(() => {
    const { selectedGroup } = store.state;
    if (!selectedGroup) {
      return false;
    }

    const groupAsset = findAssetForGroupID(selectedGroup.groupID, store.state);
    return canUserDeleteAllAssets(groupAsset ? [groupAsset] : [], userID);
  }, [store, userID]);

  const requestMoveToTrash = useCallback(
    (assets: AssetLibraryDisplayItem | AssetLibraryDisplayItem[]) => {
      const assetsArray = Array.isArray(assets) ? assets : [assets];

      if (!canUserDeleteAllAssets(assetsArray, userID)) {
        return;
      }

      const targets = resolveDeleteTargetsFromAssets(assetsArray);
      if (targets.length === 0) {
        return;
      }

      setPendingDeleteTargets(targets);
      setTrashModalOpen(true);
    },
    [resolveDeleteTargetsFromAssets, userID],
  );

  const confirmMoveToTrash = useCallback(async () => {
    if (pendingDeleteTargets.length === 0) {
      return;
    }

    setTrashModalLoading(true);
    try {
      const result = await executePatchTargets(pendingDeleteTargets, 'DELETE');
      if (result !== 'failed') {
        setTrashModalOpen(false);
        setPendingDeleteTargets([]);
      }
    } finally {
      setTrashModalLoading(false);
    }
  }, [executePatchTargets, pendingDeleteTargets]);

  const requestRestoreFromTrash = useCallback(
    async (assets: AssetLibraryDisplayItem | AssetLibraryDisplayItem[]) => {
      const assetsArray = Array.isArray(assets) ? assets : [assets];

      if (!canUserDeleteAllAssets(assetsArray, userID)) {
        return;
      }

      const targets = resolveDeleteTargetsFromAssets(assetsArray);
      if (targets.length === 0) {
        return;
      }

      await executePatchTargets(targets, 'RESTORE');
    },
    [executePatchTargets, resolveDeleteTargetsFromAssets, userID],
  );

  const toggleBookmark = useCallback(
    async (asset: AssetLibraryDisplayItem) => {
      const { apiKey, userID: actionUserID } = getActionContext();
      if (!apiKey || !actionUserID) {
        noticesStore.error('Unable to authenticate. Please try again.');
        return;
      }

      const request = resolveAssetLibraryBookmarkRequest(asset);
      const referenceAssets = getBookmarkReferenceAssets(store.state);
      const nextBookmarked = !resolveAssetBookmarkedState(
        asset,
        referenceAssets,
      );

      const { error } = await setAssetBookmarked(request, {
        apiKey,
        userID: actionUserID,
        bookmarked: nextBookmarked,
      });

      if (error) {
        noticesStore.error(error.message || 'Unable to update bookmark.');
        return;
      }

      store.applyBookmarkChange(request, nextBookmarked, asset);
      store.setPreviewBookmarkState(nextBookmarked);
    },
    [getActionContext, store],
  );

  const bookmarkAllSelected = useCallback(async () => {
    const selectedAssets = store.getSelectedDisplayAssets();
    if (selectedAssets.length === 0) {
      return;
    }

    const { apiKey, userID: actionUserID } = getActionContext();
    if (!apiKey || !actionUserID) {
      noticesStore.error('Unable to authenticate. Please try again.');
      return;
    }

    const requests = dedupeBookmarkRequests(
      selectedAssets.map((asset) => resolveAssetLibraryBookmarkRequest(asset)),
    );

    for (const request of requests) {
      const { error } = await setAssetBookmarked(request, {
        apiKey,
        userID: actionUserID,
        bookmarked: true,
      });

      if (error) {
        noticesStore.error(error.message || 'Unable to bookmark items.');
        return;
      }

      const sourceAsset = selectedAssets.find((asset) =>
        assetMatchesBookmarkRequest(asset, request),
      );
      store.applyBookmarkChange(request, true, sourceAsset);
    }

    store.clearAssetSelection();
  }, [getActionContext, store]);

  const requestMoveGroupToTrash = useCallback(() => {
    const { selectedGroup } = store.state;
    if (!selectedGroup || !canMutateGroup()) {
      return;
    }

    setPendingDeleteTargets([resolveGroupSelectionDeleteTarget(selectedGroup)]);
    setTrashModalOpen(true);
  }, [canMutateGroup, store]);

  const requestRestoreGroupFromTrash = useCallback(async () => {
    const { selectedGroup } = store.state;
    if (!selectedGroup || !canMutateGroup()) {
      return;
    }

    await executePatchTargets(
      [resolveGroupSelectionDeleteTarget(selectedGroup)],
      'RESTORE',
    );
  }, [canMutateGroup, executePatchTargets, store]);

  const value = useMemo(
    () => ({
      deleteOwnItemTooltip: ASSET_LIBRARY_DELETE_OWN_ITEM_TOOLTIP,
      restoreOwnItemTooltip: ASSET_LIBRARY_RESTORE_OWN_ITEM_TOOLTIP,
      canDeleteAsset: (asset: AssetLibraryDeletableItem | undefined) =>
        canUserDeleteAsset(asset, userID),
      canDeleteAllAssets: (assets: AssetLibraryDeletableItem[]) =>
        canUserDeleteAllAssets(assets, userID),
      requestMoveToTrash,
      requestRestoreFromTrash,
      toggleBookmark,
      bookmarkAllSelected,
      requestMoveGroupToTrash,
      requestRestoreGroupFromTrash,
    }),
    [
      bookmarkAllSelected,
      requestMoveGroupToTrash,
      requestMoveToTrash,
      requestRestoreFromTrash,
      requestRestoreGroupFromTrash,
      toggleBookmark,
      userID,
    ],
  );

  return (
    <AssetLibraryAssetActionsContext.Provider value={value}>
      {children}
      <AssetLibraryMoveToTrashModal
        open={trashModalOpen}
        loading={trashModalLoading}
        onOpenChange={setTrashModalOpen}
        onConfirm={() => {
          void confirmMoveToTrash();
        }}
      />
    </AssetLibraryAssetActionsContext.Provider>
  );
}

export function useAssetLibraryAssetActions() {
  const context = useContext(AssetLibraryAssetActionsContext);
  if (!context) {
    throw new Error(
      'useAssetLibraryAssetActions must be used within AssetLibraryAssetActionsProvider',
    );
  }

  return context;
}
